/**
 * FilterBuilder.js
 *
 * Form-based predicate filter builder for the dlr-builder mode.
 * Lets developers define filter conditions as a structured form —
 * no raw JSON editing required.
 *
 * When `filterableFields` is provided (from schema json_schema.properties),
 * the builder becomes type-aware: operators and value inputs adapt to each
 * field's type (string, number, boolean, enum, date).
 *
 * Output shape:
 *   { fieldName: { in|eq|ne|gt|lt|gte|lte: value(s) } }
 *
 * Operator availability by type:
 *   string  → eq, ne, in
 *   enum    → eq, ne, in (values from enum list)
 *   boolean → eq          (value: true/false dropdown)
 *   number  → eq, ne, gt, gte, lt, lte
 *   date    → eq, gt, gte, lt, lte
 */
import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Type helpers
// ---------------------------------------------------------------------------

function getTypeCategory(fieldDef) {
  if (!fieldDef) return 'string';
  if (fieldDef.enum && fieldDef.enum.length > 0) return 'enum';
  if (fieldDef.type === 'boolean') return 'boolean';
  if (fieldDef.type === 'integer' || fieldDef.type === 'number') return 'number';
  if (fieldDef.format === 'date' || fieldDef.format === 'date-time') return 'date';
  return 'string';
}

const OPERATORS_BY_TYPE = {
  string:  [
    { value: 'eq',  label: 'is' },
    { value: 'ne',  label: 'is not' },
    { value: 'in',  label: 'is one of' },
  ],
  enum: [
    { value: 'eq',  label: 'is' },
    { value: 'ne',  label: 'is not' },
    { value: 'in',  label: 'is one of' },
  ],
  boolean: [
    { value: 'eq',  label: 'is' },
  ],
  number: [
    { value: 'eq',  label: '=' },
    { value: 'ne',  label: '≠' },
    { value: 'gt',  label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt',  label: '<' },
    { value: 'lte', label: '≤' },
  ],
  date: [
    { value: 'eq',  label: 'on' },
    { value: 'gt',  label: 'after' },
    { value: 'gte', label: 'on or after' },
    { value: 'lt',  label: 'before' },
    { value: 'lte', label: 'on or before' },
  ],
};

// ---------------------------------------------------------------------------
// Filter parsing
// ---------------------------------------------------------------------------

const ALL_OPS = ['in', 'eq', 'ne', 'gt', 'gte', 'lt', 'lte'];

/**
 * Parse a filter object into an array of condition rows for the form.
 * { metric_type: { in: ["heart_rate"] }, value: { gt: 72 } }
 * →
 * [
 *   { field: "metric_type", op: "in",  values: ["heart_rate"] },
 *   { field: "value",       op: "gt",  values: ["72"] },
 * ]
 */
function filterToRows(filter) {
  if (!filter || typeof filter !== 'object') return [];
  return Object.entries(filter).map(([field, predicate]) => {
    if (typeof predicate === 'string' || typeof predicate === 'number' || typeof predicate === 'boolean') {
      return { field, op: 'eq', values: [String(predicate)] };
    }
    for (const op of ALL_OPS) {
      if (op in predicate) {
        const raw = predicate[op];
        const values = Array.isArray(raw) ? raw.map(String) : [String(raw)];
        return { field, op, values };
      }
    }
    return { field, op: 'eq', values: [JSON.stringify(predicate)] };
  });
}

/**
 * Convert condition rows back to a filter object, coercing value types.
 */
function rowsToFilter(rows, fieldsMap = {}) {
  const result = {};
  for (const row of rows) {
    if (!row.field || !row.field.trim() || row.values.length === 0) continue;
    const typeCategory = fieldsMap[row.field] || 'string';

    const coerce = (v) => {
      if (v === '' || v === null || v === undefined) return v;
      if (typeCategory === 'number') {
        const n = Number(v);
        return isNaN(n) ? v : n;
      }
      if (typeCategory === 'boolean') {
        return v === 'true' || v === true;
      }
      return v; // string, enum, date → keep as-is
    };

    if (row.op === 'in') {
      const coerced = row.values.map(coerce).filter(v => v !== '');
      if (coerced.length === 0) continue;
      result[row.field] = { in: coerced };
    } else {
      const coerced = coerce(row.values[0]);
      if (coerced === '' || coerced === undefined) continue;
      result[row.field] = { [row.op]: coerced };
    }
  }
  return Object.keys(result).length > 0 ? result : null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const baseInputStyle = {
  fontSize: '12px',
  padding: '4px 8px',
  border: '1px solid var(--border-color)',
  borderRadius: '5px',
  background: 'var(--bg-card)',
  color: 'var(--text-primary)',
  outline: 'none',
  transition: 'border-color 0.12s',
};

const chipStyle = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: "ui-monospace, 'SF Mono', 'Fira Code', Consolas, monospace",
  fontSize: '12px',
  padding: '2px 8px',
  borderRadius: '5px',
  border: `1px solid ${active ? 'var(--primary)' : 'var(--border-color)'}`,
  background: active ? 'var(--primary-light)' : 'var(--bg-card)',
  color: active ? 'var(--primary)' : 'var(--text-secondary)',
  cursor: 'default',
});

const removeBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  fontSize: '11px',
  padding: '0 1px',
  lineHeight: 1,
};

// ---------------------------------------------------------------------------
// Value input — type-aware
// ---------------------------------------------------------------------------

function ValueInput({ row, idx, fieldDef, valueInput, setValueInput, updateRowValues, addValue }) {
  const typeCategory = getTypeCategory(fieldDef);
  const isMulti = row.op === 'in';

  // Boolean: single dropdown
  if (typeCategory === 'boolean') {
    return (
      <select
        style={{ ...baseInputStyle, cursor: 'pointer' }}
        value={row.values[0] || ''}
        onChange={e => updateRowValues(idx, [e.target.value])}
      >
        <option value="">— select —</option>
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  // Enum: dropdown for eq/ne, toggleable chips for in
  if (typeCategory === 'enum' && fieldDef?.enum) {
    if (!isMulti) {
      return (
        <select
          style={{ ...baseInputStyle, cursor: 'pointer' }}
          value={row.values[0] || ''}
          onChange={e => updateRowValues(idx, [e.target.value])}
        >
          <option value="">— select —</option>
          {fieldDef.enum.map(v => (
            <option key={v} value={String(v)}>{String(v)}</option>
          ))}
        </select>
      );
    }
    // is one of → toggleable chips from enum list
    const selected = new Set(row.values);
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {fieldDef.enum.map(v => {
          const sv = String(v);
          const on = selected.has(sv);
          return (
            <button className="btn"
              key={sv}
              type="button"
              style={{ ...chipStyle(on), cursor: 'pointer' }}
              onClick={() => {
                const next = on
                  ? row.values.filter(x => x !== sv)
                  : [...row.values, sv];
                updateRowValues(idx, next);
              }}
            >
              {sv}
            </button>
          );
        })}
      </div>
    );
  }

  // Number
  if (typeCategory === 'number') {
    return (
      <>
        {isMulti && row.values.map((v, vi) => (
          <span key={vi} style={chipStyle(true)}>
            {v}
            <button className="btn" type="button" style={removeBtn} onClick={() => {
              updateRowValues(idx, row.values.filter((_, i) => i !== vi));
            }}>×</button>
          </span>
        ))}
        <input
          type="number"
          style={{ ...baseInputStyle, width: isMulti ? 90 : 110, fontFamily: "ui-monospace, monospace" }}
          value={isMulti ? (valueInput[idx] || '') : (row.values[0] || '')}
          placeholder={isMulti ? 'add value…' : 'number'}
          onChange={e => {
            if (isMulti) {
              setValueInput(prev => ({ ...prev, [idx]: e.target.value }));
            } else {
              updateRowValues(idx, [e.target.value]);
            }
          }}
          onKeyDown={isMulti ? (e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addValue(idx);
            }
          }) : undefined}
          onBlur={isMulti ? (() => {
            if ((valueInput[idx] || '').trim()) addValue(idx);
          }) : undefined}
        />
      </>
    );
  }

  // Date
  if (typeCategory === 'date') {
    const inputType = fieldDef?.format === 'date-time' ? 'datetime-local' : 'date';
    return (
      <input
        type={inputType}
        style={{ ...baseInputStyle, width: inputType === 'datetime-local' ? 180 : 130 }}
        value={row.values[0] || ''}
        onChange={e => updateRowValues(idx, [e.target.value])}
      />
    );
  }

  // String (default) — chip input for in, text for eq/ne
  return (
    <>
      {isMulti && row.values.map((v, vi) => (
        <span key={vi} style={chipStyle(true)}>
          {v}
          <button className="btn" type="button" style={removeBtn} onClick={() => {
            updateRowValues(idx, row.values.filter((_, i) => i !== vi));
          }}>×</button>
        </span>
      ))}
      <input
        style={{
          ...baseInputStyle,
          width: isMulti ? 110 : 150,
          fontFamily: "ui-monospace, monospace",
        }}
        value={isMulti ? (valueInput[idx] || '') : (row.values[0] || '')}
        placeholder={isMulti ? 'add value…' : 'value'}
        onChange={e => {
          if (isMulti) {
            setValueInput(prev => ({ ...prev, [idx]: e.target.value }));
          } else {
            updateRowValues(idx, [e.target.value]);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            if (isMulti) {
              addValue(idx);
            } else {
              const val = (e.target.value || '').trim();
              if (val) updateRowValues(idx, [val]);
            }
          }
        }}
        onBlur={() => {
          const val = (valueInput[idx] || '').trim();
          if (val && isMulti) addValue(idx);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Free-text add row (fallback when no schema available)
// ---------------------------------------------------------------------------

function FreeTextAddRow({ onAdd }) {
  const [draft, setDraft] = useState('');
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        style={{
          fontSize: '12px', padding: '4px 8px',
          border: '1px solid var(--border-color)', borderRadius: '5px',
          background: 'var(--bg-card)', color: 'var(--text-primary)',
          outline: 'none', flex: 1,
          fontFamily: "ui-monospace, monospace",
        }}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder="field name (e.g. metric_type)"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (draft.trim()) { onAdd(draft.trim()); setDraft(''); }
          }
        }}
      />
      <button className="btn"
        type="button"
        style={{
          fontSize: 12, fontWeight: 600, padding: '5px 12px',
          borderRadius: 5, border: '1px dashed var(--primary)',
          background: draft.trim() ? 'var(--primary-light)' : 'transparent',
          color: 'var(--primary)',
          cursor: draft.trim() ? 'pointer' : 'not-allowed',
          opacity: draft.trim() ? 1 : 0.5,
          transition: 'all 0.12s', whiteSpace: 'nowrap',
        }}
        onClick={() => { if (draft.trim()) { onAdd(draft.trim()); setDraft(''); } }}
        disabled={!draft.trim()}
      >
        + Add condition
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @param {object|null}  filter           Current filter value
 * @param {function}     onChange         Called with updated filter (or null)
 * @param {boolean}      readOnly         Render as read-only badges
 * @param {Array|null}   filterableFields Schema fields: [{ key, type, format, enum, encrypted }]
 *                                        When provided, enables type-aware inputs + field dropdown.
 */
export default function FilterBuilder({ filter, onChange, readOnly = false, filterableFields = null }) {
  const [rows, setRows] = useState(() => filterToRows(filter));
  const [valueInput, setValueInput] = useState({}); // rowIdx → current chip input

  // Build field → typeCategory map for coercion on emit
  const fieldsMap = filterableFields
    ? Object.fromEntries(filterableFields.map(f => [f.key, getTypeCategory(f)]))
    : {};

  const emit = (nextRows) => {
    onChange(rowsToFilter(nextRows, fieldsMap));
  };

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    setRows(next);
    emit(next);
  };

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => i === idx ? { ...r, ...patch } : r);
    setRows(next);
    emit(next);
  };

  const updateRowValues = (idx, values) => {
    const next = rows.map((r, i) => i === idx ? { ...r, values } : r);
    setRows(next);
    emit(next);
  };

  const addValue = (idx) => {
    const val = (valueInput[idx] || '').trim();
    if (!val) return;
    const row = rows[idx];
    const next = rows.map((r, i) => i === idx ? { ...r, values: [...r.values, val] } : r);
    setRows(next);
    emit(next);
    setValueInput(prev => ({ ...prev, [idx]: '' }));
  };

  const addRowForField = (fieldKey) => {
    if (!fieldKey) return;
    const fieldDef = filterableFields?.find(f => f.key === fieldKey);
    const typeCategory = getTypeCategory(fieldDef);
    const op = OPERATORS_BY_TYPE[typeCategory]?.[0]?.value || 'eq';
    const next = [...rows, { field: fieldKey, op, values: [] }];
    setRows(next);
    emit(next);
  };

  // ---------------------------------------------------------------------------
  // Read-only display
  // ---------------------------------------------------------------------------
  if (readOnly) {
    const displayRows = filterToRows(filter);
    if (displayRows.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {displayRows.map((row, i) => {
          const fieldDef = filterableFields?.find(f => f.key === row.field);
          const typeCategory = getTypeCategory(fieldDef);
          const ops = OPERATORS_BY_TYPE[typeCategory] || OPERATORS_BY_TYPE.string;
          const opLabel = ops.find(o => o.value === row.op)?.label || row.op;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5 }}>
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>
                {row.field}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{opLabel}</span>
              {row.values.map((v, vi) => (
                <span key={vi} style={chipStyle(true)}>{v}</span>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Editable form
  // ---------------------------------------------------------------------------

  const usedFields = new Set(rows.map(r => r.field));
  const availableFields = filterableFields
    ? filterableFields.filter(f => !f.encrypted && !usedFields.has(f.key))
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* Existing rows */}
      {rows.map((row, idx) => {
        const fieldDef = filterableFields?.find(f => f.key === row.field);
        const typeCategory = getTypeCategory(fieldDef);
        const operators = OPERATORS_BY_TYPE[typeCategory] || OPERATORS_BY_TYPE.string;

        return (
          <div key={idx} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            padding: '4px 0',
          }}>
            {/* Field name */}
            {filterableFields ? (
              <span style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-primary)',
                padding: '3px 7px',
                background: 'var(--bg-hover, rgba(0,0,0,0.04))',
                borderRadius: 4,
                whiteSpace: 'nowrap',
              }}>
                {row.field}
              </span>
            ) : (
              <input
                style={{ ...baseInputStyle, width: 100, fontFamily: "ui-monospace, monospace" }}
                value={row.field}
                onChange={e => updateRow(idx, { field: e.target.value })}
                placeholder="field"
              />
            )}

            {/* Operator */}
            <select
              style={{ ...baseInputStyle, cursor: 'pointer', padding: '3px 6px', fontSize: 11 }}
              value={row.op}
              onChange={e => updateRow(idx, { op: e.target.value, values: [] })}
            >
              {operators.map(op => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </select>

            {/* Values — inline */}
            <ValueInput
              row={row}
              idx={idx}
              fieldDef={fieldDef}
              valueInput={valueInput}
              setValueInput={setValueInput}
              updateRowValues={updateRowValues}
              addValue={addValue}
            />

            {/* Remove row */}
            <button className="btn"
              type="button"
              style={{ ...removeBtn, fontSize: 13, color: 'var(--danger)', padding: '0 3px', flexShrink: 0 }}
              onClick={() => removeRow(idx)}
              title="Remove condition"
            >
              ×
            </button>
          </div>
        );
      })}

      {/* Add new condition */}
      {filterableFields ? (
        availableFields && availableFields.length > 0 ? (
          <select
            style={{
              ...baseInputStyle,
              cursor: 'pointer',
              color: 'var(--primary)',
              border: '1px dashed var(--primary)',
              background: 'transparent',
              fontSize: 11,
              padding: '2px 6px',
            }}
            value=""
            onChange={e => { if (e.target.value) addRowForField(e.target.value); }}
          >
            <option value="">+ Add condition…</option>
            {availableFields.map(f => (
              <option key={f.key} value={f.key}>
                {f.key}{f.type ? ` (${f.format || f.type})` : ''}
              </option>
            ))}
          </select>
        ) : (
          rows.length > 0 && (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>
              All schema fields have conditions defined.
            </p>
          )
        )
      ) : (
        <FreeTextAddRow onAdd={(field) => {
          if (usedFields.has(field)) return;
          const next = [...rows, { field, op: 'eq', values: [] }];
          setRows(next);
          emit(next);
        }} />
      )}
    </div>
  );
}
